import { getAdminClient } from "@/lib/supabase-admin";
import { compressImage } from "@/lib/image-compress";
import type { Tables, TablesUpdate } from "@/lib/supabase.types";

export type ProductRow = Tables<"products">;
export type VariantRow = Tables<"product_variants">;
export type ImageRow = Tables<"product_images">;

export interface ProductWithRelations {
  product: ProductRow;
  variants: VariantRow[];
  images: ImageRow[];
}

export interface ProductSummary extends ProductRow {
  variant_count: number;
  active_variant_count: number;
  min_price_ore: number | null;
  max_price_ore: number | null;
  total_inventory: number | null;
  image_count: number;
}

const NOK = new Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export function formatOre(ore: number | null | undefined): string {
  return NOK.format((ore ?? 0) / 100);
}

/**
 * Resolves a product image `storage_path` to a usable URL. Same fallback
 * pattern as events: absolute URLs and `/public`-paths pass through, else
 * we resolve against the product-images bucket.
 */
export function resolveProductImageUrl(storagePath: string | null): string | null {
  if (!storagePath) return null;
  if (storagePath.startsWith("http://") || storagePath.startsWith("https://")) return storagePath;
  if (storagePath.startsWith("/")) return storagePath;
  const url = import.meta.env.VITE_SUPABASE_URL?.trim();
  if (!url) return null;
  return `${url.replace(/\/$/, "")}/storage/v1/object/public/product-images/${storagePath.replace(/^\//, "")}`;
}

export async function fetchAllProductsWithStats(): Promise<ProductSummary[]> {
  const supabase = getAdminClient();
  const [productsRes, variantsRes, imagesRes] = await Promise.all([
    supabase.from("products").select("*").order("sort_order", { ascending: true }),
    supabase.from("product_variants").select("product_id, price_ore, inventory, is_active"),
    supabase.from("product_images").select("product_id"),
  ]);
  if (productsRes.error) throw productsRes.error;
  if (variantsRes.error) throw variantsRes.error;
  if (imagesRes.error) throw imagesRes.error;

  type V = { product_id: string; price_ore: number; inventory: number | null; is_active: boolean };
  const variantsByProduct = new Map<string, V[]>();
  for (const v of (variantsRes.data ?? []) as V[]) {
    const list = variantsByProduct.get(v.product_id) ?? [];
    list.push(v);
    variantsByProduct.set(v.product_id, list);
  }

  const imageCounts = new Map<string, number>();
  for (const i of imagesRes.data ?? []) {
    imageCounts.set(i.product_id, (imageCounts.get(i.product_id) ?? 0) + 1);
  }

  return (productsRes.data ?? []).map((p) => {
    const vs = variantsByProduct.get(p.id) ?? [];
    const prices = vs.map((v) => v.price_ore);
    const inventories = vs.map((v) => v.inventory).filter((n): n is number => n != null);
    return {
      ...p,
      variant_count: vs.length,
      active_variant_count: vs.filter((v) => v.is_active).length,
      min_price_ore: prices.length ? Math.min(...prices) : null,
      max_price_ore: prices.length ? Math.max(...prices) : null,
      total_inventory: inventories.length ? inventories.reduce((a, b) => a + b, 0) : null,
      image_count: imageCounts.get(p.id) ?? 0,
    };
  });
}

export async function fetchProductDetail(id: string): Promise<ProductWithRelations | null> {
  const supabase = getAdminClient();
  const { data: product, error: pErr } = await supabase
    .from("products")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (pErr) throw pErr;
  if (!product) return null;

  const [vRes, iRes] = await Promise.all([
    supabase
      .from("product_variants")
      .select("*")
      .eq("product_id", id)
      .order("sort_order", { ascending: true }),
    supabase
      .from("product_images")
      .select("*")
      .eq("product_id", id)
      .order("sort_order", { ascending: true }),
  ]);
  if (vRes.error) throw vRes.error;
  if (iRes.error) throw iRes.error;

  return {
    product,
    variants: vRes.data ?? [],
    images: iRes.data ?? [],
  };
}

export async function updateProduct(
  id: string,
  patch: TablesUpdate<"products">
): Promise<void> {
  const { error } = await getAdminClient().from("products").update(patch).eq("id", id);
  if (error) throw error;
}

export async function updateVariant(
  id: string,
  patch: TablesUpdate<"product_variants">
): Promise<void> {
  const { error } = await getAdminClient().from("product_variants").update(patch).eq("id", id);
  if (error) throw error;
}

export async function uploadProductImage(productId: string, file: File): Promise<void> {
  const compressed = await compressImage(file);
  const ext = compressed.name.split(".").pop()?.toLowerCase() ?? "jpg";
  const key = `${productId}/${crypto.randomUUID()}.${ext}`;
  const supabase = getAdminClient();
  const { error: upErr } = await supabase
    .storage.from("product-images")
    .upload(key, compressed, { contentType: compressed.type, upsert: false });
  if (upErr) throw upErr;
  const { error: insErr } = await supabase.from("product_images").insert({
    product_id: productId,
    storage_path: key,
  });
  if (insErr) throw insErr;
}

export async function deleteProductImage(image: ImageRow): Promise<void> {
  const supabase = getAdminClient();
  // If the row points at a storage key (not an absolute URL), remove the
  // object too. Legacy rows pointing at Shopify CDN are left alone.
  const key = image.storage_path;
  const looksLikeStorageKey =
    key && !key.startsWith("http://") && !key.startsWith("https://") && !key.startsWith("/");
  if (looksLikeStorageKey) {
    await supabase.storage.from("product-images").remove([key]);
  }
  const { error } = await supabase.from("product_images").delete().eq("id", image.id);
  if (error) throw error;
}

export async function updateImageSortOrder(id: string, sortOrder: number): Promise<void> {
  const { error } = await getAdminClient()
    .from("product_images")
    .update({ sort_order: sortOrder })
    .eq("id", id);
  if (error) throw error;
}

/** URL- og SKU-vennlig slug fra tittel. */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/æ/g, "ae")
    .replace(/ø/g, "o")
    .replace(/å/g, "a")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function generateSku(title: string): string {
  const base = slugify(title)
    .toUpperCase()
    .replace(/-/g, "")
    .slice(0, 12);
  // Lite suffiks for unikhet hvis flere produkter har lignende navn.
  const suffix = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, "0");
  return `${base || "PROD"}-${suffix}`;
}

export interface CreateProductInput {
  title: string;
  category: string;
  priceOre: number;
  isActive: boolean;
}

/** Oppretter et produkt + en default-variant og returnerer det nye produkt-ID-en. */
export async function createProduct(input: CreateProductInput): Promise<string> {
  const supabase = getAdminClient();
  const slug = slugify(input.title);
  // Finn høyeste sort_order så ny produkt legges sist.
  const { data: max } = await supabase
    .from("products")
    .select("sort_order")
    .order("sort_order", { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrder = (max?.sort_order ?? 0) + 1;

  const { data: product, error: pErr } = await supabase
    .from("products")
    .insert({
      title: input.title.trim(),
      slug,
      category: input.category.trim(),
      is_active: input.isActive,
      sort_order: nextOrder,
    })
    .select("id, title")
    .single();
  if (pErr || !product) throw pErr ?? new Error("Kunne ikke opprette produkt");

  const { error: vErr } = await supabase.from("product_variants").insert({
    product_id: product.id,
    title: "Default",
    sku: generateSku(product.title),
    price_ore: input.priceOre,
    is_active: input.isActive,
  });
  if (vErr) throw vErr;

  return product.id;
}

/**
 * Bytt sort_order mellom to produkter for å reordere i tabellen. To separate
 * update-kall — Supabase JS-klienten støtter ikke transaksjon her, men
 * worst case er midlertidig duplikat sort_order, ikke datatap.
 */
export async function swapSortOrder(
  a: { id: string; sort_order: number },
  b: { id: string; sort_order: number }
): Promise<void> {
  const supabase = getAdminClient();
  const aRes = await supabase
    .from("products")
    .update({ sort_order: b.sort_order })
    .eq("id", a.id);
  if (aRes.error) throw aRes.error;
  const bRes = await supabase
    .from("products")
    .update({ sort_order: a.sort_order })
    .eq("id", b.id);
  if (bRes.error) throw bRes.error;
}
